import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { IconCross } from '@/app/common/icons/libraryIcons';

import { useIsTabletOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import {
  getImagePictureSizes,
  getImageSources,
  getImageUrl,
  ImageSliderFullScreen,
} from '@/app/components/hotel-detail/ImageSlider';

import customCss from '@/app/components/hotel-detail/css/customCss';
import { useFocusTrap } from '@/app/common/hooks/useFocusTrap';
import { HotelGalleryGoToRoomsButton } from './GoToRoomsButton';

const CloseButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 18px;
  color: ${({ theme }) => theme.commons.colors.gray0};
  border: none;
  background: transparent;
  padding: 8px;
  margin-left: -8px;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  & > [data-library-icon] {
    width: 18px;
    height: 18px;
  }
`;

type ImageGalleryModalXProps = {
  navigateToRooms?: boolean;
  images: readonly string[];
  initialFocussedImageIndex?: number;
  isOpen?: boolean;
  onDismiss?: (event?: React.SyntheticEvent<Element, Event>) => void;
  onShowRoomsClick?: () => void;
};

export default function ImageGalleryModalX(props: ImageGalleryModalXProps) {
  const {
    initialFocussedImageIndex = 0,
    images,
    isOpen,
    onDismiss,
    navigateToRooms = false,
    onShowRoomsClick = () => {},
  } = props;
  const [slideIndex, setSlideIndex] = useState(initialFocussedImageIndex);
  const rootRef = useRef<HTMLDivElement>(null);

  useFocusTrap({ active: !!isOpen, getContainer: () => rootRef.current });

  const isMobile = !useIsTabletOrGreater();

  const onSlideChange = (index: number) => {
    setSlideIndex(index);
  };

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      inert={!isOpen}
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background: ${({ theme }) => theme.commons.colors.gray800};
        transform: ${isOpen ? 'translate(0, 0)' : 'translate(0, 100%)'};
        opacity: ${isOpen ? 1 : 0};
        transition: all 0.3s ease-in-out;
      `}
    >
      <div
        css={css`
          padding: 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 56px;
          @media ${customCss.breakpoints.sm} {
            height: 64px;
          }
          z-index: 1;
        `}
      >
        <CloseButton onClick={onDismiss} aria-label="close gallery">
          <IconCross />
        </CloseButton>
        {navigateToRooms && (
          <div
            css={`
              display: none;
              @media ${customCss.breakpoints.sm} {
                display: block;
              }
              @media ${customCss.breakpoints.mobileLandscape} {
                display: none;
              }
            `}
          >
            <HotelGalleryGoToRoomsButton onClick={onShowRoomsClick} />
          </div>
        )}
        <ImageCounter
          css={`
            z-index: -1;
            position: absolute;
            left: 50%;
            transform: translate(-50%);
          `}
        >{`${slideIndex + 1} / ${images.length}`}</ImageCounter>
      </div>

      <div
        css={css`
          flex: 1 0 0;
          overflow: hidden;

          @media ${customCss.breakpoints.mobileLandscape} {
            position: absolute;
            top: 0;
            left: 0;
          }
        `}
      >
        {isOpen && (
          <ImageSliderFullScreen
            images={images}
            imageIndex={initialFocussedImageIndex}
            css={css`
              height: 100%;

              & .image-slide {
                object-fit: contain;

                @media ${customCss.breakpoints.sm} {
                  padding: 0 96px 68px;
                }

                @media ${customCss.breakpoints.xl} {
                  padding: 0 160px 68px;
                }

                @media ${customCss.breakpoints.mobileLandscape} {
                  padding: 0 80px;
                }
              }

              & .slider-overlay-arrow-left,
              & .slider-overlay-arrow-right {
                display: none;

                background: rgba(255, 255, 255, 0.1);
                border-radius: 48px;
                width: 48px;
                height: 48px;
                align-items: center;

                &:hover {
                  background: rgba(255, 255, 255, 0.4);
                }

                & > [data-library-icon] {
                  width: 20px;
                  height: 20px;
                }

                @media ${customCss.breakpoints.sm} {
                  display: flex;
                }
              }

              & .slider-overlay-arrow-left {
                left: 24px;
                @media ${customCss.breakpoints.mobileLandscape} {
                  left: 16px;
                }
              }

              & .slider-overlay-arrow-right {
                right: 24px;
                @media ${customCss.breakpoints.mobileLandscape} {
                  right: 16px;
                }
              }

              @media ${customCss.breakpoints.mobileLandscape} {
                height: 100vh;
                width: 100vw;
              }
            `}
            transformations={{ url: getImageUrl, sources: getImageSources, sizes: getImagePictureSizes }}
            onSlideChange={onSlideChange}
            counterDisplay="none"
            animation={isMobile ? 'scroll' : 'none'}
          />
        )}
      </div>
      <div
        css={`
          width: 100%;
          padding: 12px 16px;
          height: 64px;
          @media ${customCss.breakpoints.mobileLandscape} {
            display: none;
          }
          @media ${customCss.breakpoints.sm} {
            display: none;
          }
        `}
      >
        {isMobile && navigateToRooms && (
          <HotelGalleryGoToRoomsButton
            onClick={onShowRoomsClick}
            css={`
              width: 100%;
            `}
          />
        )}
      </div>
    </div>
  );
}

const ImageCounter = styled.div`
  width: 100%;
  height: 64px;
  align-items: center;
  justify-content: center;
  display: flex;
  font-size: ${(p) => p.theme.commons.font.sizes.base};
  color: ${(p) => p.theme.commons.colors.gray0};

  @media ${customCss.breakpoints.sm} {
    font-size: ${(p) => p.theme.commons.font.sizes.medium};
    background: linear-gradient(180deg, rgba(60, 60, 60, 0.6) 50%, rgba(60, 60, 60, 0) 100%);
  }
`;
